"use client";

import React, { useEffect, useState, useMemo } from "react";
import Image from "next/image";
import { Check, ShoppingCart, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ecoBuilder/ui/button";
import { Badge } from "@/components/ecoBuilder/ui/badge";
import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { createClient } from "@/utils/supabase/client";
import { RichTextRenderer } from "@/components/ecoBuilder/atoms/RichTextRenderer";
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ALGERIAN_WILAYAS } from '@/lib/data/wilayas';
import { OFFICIAL_COMMUNES } from '@/lib/data/official-locations';
import { Input } from '@/components/ecoBuilder/ui/input';
import { Label } from '@/components/ecoBuilder/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ecoBuilder/ui/select';
import { createOrder } from '@/actions/order-actions';
import { toast } from 'sonner';
import { useSiteContext } from '@/app/tenant/TenantLayoutWrapper';
import { PaddingHandle } from "@/components/ecoBuilder/editor/PaddingHandle";
import { useEcommerce } from "@/hooks/useEcommerce";

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

const phoneRegex = /^(05|06|07)[0-9]{8}$/;

export interface EcoBuyCardProps {
    productId?: string;
    layout?: "image-left" | "image-right";

    // Customization
    title?: string;
    description?: string;
    buttonText?: string;

    // Form Toggles
    enableForm?: boolean;
    showName?: boolean;
    showPhone?: boolean;
    showWilaya?: boolean;
    showCommune?: boolean;

    // Styles
    backgroundColor?: string;
    textColor?: string;
    paddingTop?: number;
    paddingBottom?: number;

    // Editor Props
    isSelected?: boolean;
    onUpdateProps?: (newProps: Partial<EcoBuyCardProps>) => void;
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export const EcoBuyCard: React.FC<EcoBuyCardProps> = ({
    productId,
    layout = "image-right",
    title,
    description,
    enableForm = true,
    buttonText = "شراء المنتج الآن",
    showName = true,
    showPhone = true,
    showWilaya = true,
    showCommune = true,

    backgroundColor,
    textColor,
    paddingTop = 60,
    paddingBottom = 60,
    isSelected,
    onUpdateProps
}) => {
    const { themeSettings, siteId } = usePageContext();
    // const { siteSlug } = useSiteContext();
    // const router = useRouter();
    const ecommerce = useEcommerce(siteId || '');

    const [product, setProduct] = useState<any>(null);
    const [loadingProduct, setLoadingProduct] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [dynamicShipping, setDynamicShipping] = useState(0);

    const effectiveProductId = productId; // Product Picker returns UUID string
    const primaryColor = themeSettings?.primaryColor || '#000000';

    // Fetch Product Logic
    useEffect(() => {
        let isMounted = true;
        const loadProduct = async () => {
            // If manual card (no product ID) and no context, just stop loading
            if (!effectiveProductId && !siteId) {
                if (isMounted) setLoadingProduct(false);
                return;
            }

            try {
                // Method 1: API (if accessible)
                if (siteId && process.env.NEXT_PUBLIC_API_URL) {
                    try {
                        const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/cms/sites/${siteId}/editor-data`);
                        if (res.ok) {
                            const json = await res.json();
                            const products = json.products || [];
                            const found = effectiveProductId
                                ? products.find((p: any) => p.id === effectiveProductId)
                                : products[0];

                            if (found && isMounted) {
                                setProduct(found);
                                setLoadingProduct(false);
                                // Track View Item
                                ecommerce.viewItem({
                                    item_id: found.id,
                                    item_name: found.name,
                                    price: found.price,
                                    currency: found.currency,
                                    item_category: found.category
                                });
                                return;
                            }
                        }
                    } catch (e) {
                        // warnings ignored
                    }
                }

                // Method 2: Supabase Fallback
                const supabase = createClient();
                let query = supabase.from('products').select('*');
                if (effectiveProductId) {
                    query = query.eq('id', effectiveProductId);
                } else {
                    query = query.limit(1);
                }
                const { data } = await query.maybeSingle();

                if (isMounted && data) {
                    setProduct(data);
                    // Track View Item
                    ecommerce.viewItem({
                        item_id: data.id,
                        item_name: data.name,
                        price: data.price,
                        currency: data.currency,
                        item_category: data.category
                    });
                } else if (isMounted) {
                    setProduct(null);
                }

            } catch (err) {
                console.error("Error loading product:", err);
                setFetchError("فشل تحميل بيانات المنتج");
            } finally {
                if (isMounted) setLoadingProduct(false);
            }
        };

        loadProduct();
        return () => { isMounted = false; };
    }, [effectiveProductId, siteId]);

    // Dynamic Schema based on toggles
    const checkoutSchema = useMemo(() => {
        return z.object({
            fullName: showName ? z.string().min(2, 'الاسم مطلوب') : z.string().optional(),
            phone: showPhone ? z.string().regex(phoneRegex, 'رقم الهاتف غير صحيح (يجب أن يبدأ بـ 05, 06, 07 ويتكون من 10 أرقام)') : z.string().optional(),
            wilaya: showWilaya ? z.string().min(1, 'يرجى اختيار الولاية') : z.string().optional(),
            commune: showCommune ? z.string().optional() : z.string().optional(),
            address: z.string().optional(), // Address is now always optional in UI, handled in submission
            quantity: z.number().min(1, 'الكمية يجب أن تكون 1 على الأقل'),
        });
    }, [showName, showPhone, showWilaya, showCommune]);

    type CheckoutFormValues = z.infer<typeof checkoutSchema>;

    // Use a loose type for the form to avoid strict Zod inference issues during development
    // or explicitly type the form values
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm<CheckoutFormValues>({
        resolver: zodResolver(checkoutSchema),
        defaultValues: {
            quantity: 1,
            fullName: "",
            phone: "",
            wilaya: "",
            commune: "",
            address: ""
        }
    });

    const handleWilayaChange = (code: string) => {
        setValue('wilaya', code);
        setValue('commune', ''); // Reset commune
        const w = ALGERIAN_WILAYAS.find(wil => wil.code === code);
        if (w && w.shippingCost) setDynamicShipping(w.shippingCost);
    };

    const onSubmit: SubmitHandler<CheckoutFormValues> = async (data) => {
        if (!siteId) {
            toast.error("خطأ تقني: معرف الموقع مفقود");
            return;
        }

        setIsSubmitting(true);
        try {
            const finalPrice = product?.price || 0;
            const finalAddress = data.address && data.address.trim().length > 0 ? data.address : "العنوان غير محدد (توصيل للمكتب)";
            const finalTitle = title || product?.name || "اسم المنتج";
            const finalImage = product?.images?.[0] || product?.image_url || "/placeholder-product.jpg";

            const orderPayload = {
                customerName: data.fullName || 'Guest',
                customerPhone: data.phone || '0000000000',
                customerWilaya: data.wilaya || '',
                customerAddress: finalAddress,
                customerCommune: data.commune || '',
                items: [{
                    id: product?.id || 'manual-product',
                    name: finalTitle,
                    price: finalPrice,
                    quantity: data.quantity,
                    image: finalImage
                }],
                subtotal: finalPrice * data.quantity,
                shippingCost: dynamicShipping,
                total: (finalPrice * data.quantity) + dynamicShipping,
                paymentMethod: 'cod' as const,
                siteId: siteId
            };

            const result = await createOrder(orderPayload);
            if (result.success) {
                toast.success("تم الطلب بنجاح!");

                // Track Purchase
                // Track Purchase
                ecommerce.purchase(
                    result.orderNumber || `ORD-${Date.now()}`,
                    [{
                        item_id: product?.id || 'manual-product',
                        item_name: finalTitle,
                        price: finalPrice,
                        quantity: data.quantity
                    }],
                    (finalPrice * data.quantity) + dynamicShipping,
                    product?.currency || 'DZD'
                );

                reset();
                setIsSuccess(true);
            } else {
                toast.error(result.error || "خطأ في الطلب");
            }
        } catch (e) {
            toast.error("حدث خطأ غير متوقع");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Derived Display Values
    const displayTitle = title || product?.name || "اسم المنتج";
    const displayDescription = description || product?.description || "";
    // Image logic simplified: always product image or placeholder
    const finalImage = product?.images?.[0] || product?.image_url || "/placeholder-product.jpg";

    const finalPrice = product?.price || 0;
    const compareAt = product?.compare_at_price || 0;
    const savings = compareAt - finalPrice;

    const finalBg = backgroundColor || 'transparent';
    const finalTextColor = textColor || themeSettings?.textColor || '#000000';

    if (loadingProduct) {
        return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;
    }

    const handlePaddingChange = (side: 'top' | 'bottom') => (newVal: number) => {
        if (!onUpdateProps) return;
        if (side === 'top') onUpdateProps({ paddingTop: newVal });
        else onUpdateProps({ paddingBottom: newVal });
    };

    return (
        <section
            className={cn(
                "px-4 md:px-8 w-full relative group transition-all duration-300"
            )}
            dir="rtl"
            style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
            }}
        >
            <div
                className={cn(
                    "max-w-6xl mx-auto bg-card rounded-3xl shadow-2xl overflow-hidden border border-border/50 relative z-20",
                    "flex flex-col md:flex-row"
                )}
                style={{ backgroundColor: finalBg === 'transparent' ? 'white' : finalBg }}
            >
                {/* ----------------- Image Section ----------------- */}
                <div className={cn(
                    "relative w-full md:w-1/2 min-h-[400px] md:min-h-[600px] bg-muted",
                    layout === "image-left" ? "md:order-last" : "md:order-first"
                )}>
                    {savings > 0 && (
                        <div className="absolute top-6 right-6 z-10 bg-red-600 text-white px-4 py-2 rounded-full font-bold shadow-lg animate-bounce">
                            توفير {savings} {product?.currency || 'DZD'}
                        </div>
                    )}

                    <Image
                        src={finalImage}
                        alt="Product Image"
                        fill
                        className="object-cover hover:scale-105 transition-transform duration-700"
                        unoptimized
                    />
                </div>

                {/* ----------------- Content & Form Section ----------------- */}
                <div className="w-full md:w-1/2 p-6 md:p-10 flex flex-col justify-center">

                    <div className="mb-8 space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-primary border-primary/30 px-3 py-1">
                                الأكثر طلباً
                            </Badge>
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                                {Math.floor(Math.random() * 15) + 5} يشاهدون الآن
                            </div>
                        </div>

                        <h2
                            className="text-3xl md:text-5xl font-extrabold leading-tight tracking-tight"
                            style={{ color: finalTextColor }}
                        >
                            <RichTextRenderer html={displayTitle} inline />
                        </h2>

                        <div className="text-muted-foreground md:text-lg leading-relaxed">
                            <RichTextRenderer html={displayDescription} />
                        </div>

                        <div className="flex items-baseline gap-4 mt-4">
                            <span className="text-4xl font-black text-primary">
                                {finalPrice} <span className="text-lg font-medium text-muted-foreground">{product?.currency || 'DZD'}</span>
                            </span>
                            {compareAt > finalPrice && (
                                <span className="text-xl text-muted-foreground line-through decoration-red-500/50">
                                    {compareAt}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-8 text-sm text-muted-foreground/80">
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>توصيل سريع 58 ولاية</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>دفع عند الاستلام</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>ضمان الجودة</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>دعم فني متميز</span>
                        </div>
                    </div>

                    {enableForm ? (
                        <div className="bg-muted/30 p-6 rounded-2xl border border-border/50">
                            {isSuccess ? (
                                <div className="p-8 text-center animate-in fade-in zoom-in duration-300">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check className="w-8 h-8 text-green-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-green-800 mb-2">شكراً لك!</h3>
                                    <p className="text-green-700">تم استلام طلبك بنجاح. سنقوم بالاتصال بك قريباً لتأكيد الطلب.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    {fetchError && <p className="text-sm text-red-500 text-center mb-4 font-medium">{fetchError}</p>}
                                    {showName && (
                                        <div className="space-y-1">
                                            <Label>الاسم الكامل</Label>
                                            <Input {...register('fullName')} placeholder="أدخل اسمك" className={cn("h-11", errors.fullName && "border-red-500")} />
                                            {errors.fullName && <p className="text-xs text-red-500">{errors.fullName.message}</p>}
                                        </div>
                                    )}

                                    {showPhone && (
                                        <div className="space-y-1">
                                            <Label>رقم الإتصال</Label>
                                            <div className="relative" dir="ltr">
                                                <Input {...register('phone')} placeholder="05 XX XX XX XX" className={cn("h-11 text-left pl-4", errors.phone && "border-red-500")} />
                                            </div>
                                            {errors.phone && <p className="text-xs text-red-500">{errors.phone.message}</p>}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {showWilaya && (
                                            <div className="space-y-1">
                                                <Label>الولاية</Label>
                                                <Select onValueChange={handleWilayaChange}>
                                                    <SelectTrigger dir="rtl" className={cn("h-11 w-full text-right", errors.wilaya && "border-red-500")}>
                                                        <SelectValue placeholder="اختر ولايتك" />
                                                    </SelectTrigger>
                                                    <SelectContent dir="rtl">
                                                        {ALGERIAN_WILAYAS.map(w => (
                                                            <SelectItem key={w.code} value={w.code} className="text-right flex-row-reverse">{w.code} - {w.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {errors.wilaya && <p className="text-xs text-red-500">{errors.wilaya.message}</p>}
                                            </div>
                                        )}

                                        {showCommune && (
                                            <div className="space-y-1">
                                                <Label>البلدية</Label>
                                                <Select onValueChange={(val) => setValue('commune', val)}>
                                                    <SelectTrigger dir="rtl" className="h-11 w-full text-right" disabled={!watch('wilaya')}>
                                                        <SelectValue placeholder={watch('wilaya') ? "اختر البلدية" : "اختر الولاية أولاً"} />
                                                    </SelectTrigger>
                                                    <SelectContent dir="rtl">
                                                        {watch('wilaya') && OFFICIAL_COMMUNES[parseInt(watch('wilaya')!).toString()]?.map((commune) => (
                                                            <SelectItem key={commune} value={commune} className="text-right flex-row-reverse">
                                                                {commune}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        <Label>العنوان (اختياري)</Label>
                                        <Input {...register('address')} placeholder="الحي، رقم المنزل..." className="h-11" />
                                    </div>

                                    <div className="flex items-center justify-between bg-background p-2 rounded-lg border border-border">
                                        <span className="text-sm font-medium px-2">الكمية:</span>
                                        <div className="flex items-center gap-2">
                                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                                const c = watch('quantity');
                                                if (c > 1) setValue('quantity', c - 1);
                                            }}>-</Button>
                                            <Input {...register('quantity', { valueAsNumber: true })} type="number" className="h-8 w-12 text-center p-0 border-none" min={1} />
                                            <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                                const c = watch('quantity');
                                                setValue('quantity', c + 1);
                                            }}>+</Button>
                                        </div>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full h-14 text-lg font-bold shadow-lg mt-4 animate-in fade-in"
                                        style={{ backgroundColor: primaryColor }}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? <Loader2 className="animate-spin" /> :
                                            <div className="flex items-center gap-2">
                                                <ShoppingCart className="w-5 h-5" />
                                                <RichTextRenderer html={buttonText} />
                                            </div>
                                        }
                                    </Button>
                                </form>
                            )}
                        </div>
                    ) : (
                        <Button className="w-full h-14 text-xl font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all" style={{ backgroundColor: primaryColor }} onClick={() => {
                            // Link logic if needed, or simple scroll
                            const element = document.getElementById('checkout-form');
                            if (element) element.scrollIntoView({ behavior: 'smooth' });
                        }}>
                            <RichTextRenderer html={buttonText} />
                        </Button>
                    )}
                </div>


            </div >

            {/* Padding Handles (Only when selected) */}
            {
                isSelected && onUpdateProps && (
                    <>
                        <PaddingHandle
                            side="top"
                            currentPadding={paddingTop}
                            onPaddingChange={handlePaddingChange('top')}
                        />
                        <PaddingHandle
                            side="bottom"
                            currentPadding={paddingBottom}
                            onPaddingChange={handlePaddingChange('bottom')}
                        />
                    </>
                )
            }

        </section >
    );
};


// ------------------------------------------------------------------
// Schema
// ------------------------------------------------------------------

export const EcoBuyCardSchema = [
    {
        key: "productId",
        label: "اختر المنتج",
        type: "product",
        description: "ابحث واختر المنتج لإظهار بياناته تلقائياً"
    },
    {
        key: "layout",
        label: "تخطيط البطاقة",
        type: "select",
        options: [
            { value: "image-right", label: "الصورة يمين" },
            { value: "image-left", label: "الصورة يسار" }
        ],
        defaultValue: "image-right"
    },
    {
        key: "title",
        label: "عنوان مخصص (اختياري)",
        type: "text",
        placeholder: "اتركه فارغاً لاستخدام اسم المنتج"
    },
    {
        key: "description",
        label: "وصف مخصص (اختياري)",
        type: "richtext",
        description: "اتركه فارغاً لاستخدام وصف المنتج"
    },
    {
        key: "buttonText",
        label: "نص الزر",
        type: "richtext",
        defaultValue: "شراء المنتج الآن"
    },
    // Form Visibility Toggles
    {
        key: "enableForm",
        label: "تفعيل نموذج الشراء السريع كاملاً",
        type: "boolean",
        defaultValue: true,
        description: "عند تفعيله، سيظهر نموذج. عند تعطيله، سيظهر زر الشراء فقط (توجيه)."
    },
    {
        key: "showName",
        label: "إظهار حقل الاسم",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showPhone",
        label: "إظهار حقل الهاتف",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showWilaya",
        label: "إظهار حقل الولاية",
        type: "boolean",
        defaultValue: true
    },
    {
        key: "showCommune",
        label: "إظهار حقل البلدية",
        type: "boolean",
        defaultValue: true
    },
    // Styles
    {
        key: 'backgroundColor',
        label: 'لون الخلفية',
        type: 'color',
    },
    {
        key: 'textColor',
        label: 'لون النص',
        type: 'color',
    },
    {
        key: "paddingTop",
        label: "مسافة علوية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    },
    {
        key: "paddingBottom",
        label: "مسافة سفلية",
        type: "slider",
        defaultValue: 60,
        sliderConfig: { min: 0, max: 200, step: 4, unit: "px" }
    }
];

export const EcoBuyCardDefaultProps = {
    layout: "image-right",
    enableForm: true,
    showName: true,
    showPhone: true,
    showWilaya: true,
    showCommune: true,
    buttonText: "شراء المنتج الآن",
    backgroundColor: "#ffffff",
    paddingTop: 60,
    paddingBottom: 60,
};
